import React, { useState, useEffect, useRef } from"react";
import API_BASE_URL from"../Config/config";

const botAvatar ="https://cdn-icons-png.flaticon.com/512/4712/4712027.png";
const userAvatar ="https://randomuser.me/api/portraits/women/68.jpg";

/* ---------- SVG Icons ---------- */
const PlayIcon = ({ color ="#fff" }) => (
 <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
 <path d="M7 4l12 8-12 8V4z" fill={color} />
 </svg>
);
const PauseIcon = ({ color ="#fff" }) => (
 <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
 <rect x="6" y="4" width="4" height="16" fill={color} />
 <rect x="14" y="4" width="4" height="16" fill={color} />
 </svg>
);
const StopIcon = ({ color ="#fff" }) => (
 <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
 <rect x="6" y="6" width="12" height="12" fill={color} rx="2" />
 </svg>
);
const VolumeIcon = ({ color ="#fff" }) => (
 <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
 <path d="M5 9v6h4l5 4V5L9 9H5z" fill={color} />
 <path d="M16 8s3 2 3 4-3 4-3 4" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"/>
 </svg>
);

/* ---------- Tiny, theme-aware icon button ---------- */
function IconButton({ title, onClick, tone ="dark", size = 32, children }) {
 const [hover, setHover] = useState(false);
 const baseBorder =
 tone ==="dark" ?"1px solid rgba(252,211,77,.35)" :"1px solid rgba(26,26,26,.25)";
 const hoverBg = tone ==="dark" ?"rgba(252,211,77,.12)" :"rgba(26,26,26,.06)";
 const shadow = tone ==="dark" ?"0 0 10px rgba(252,211,77,.25)" :"none";
 return (
 <button
 type="button"
 aria-label={title}
 title={title}
 onClick={onClick}
 onMouseEnter={() => setHover(true)}
 onMouseLeave={() => setHover(false)}
 style={{
 width: size, height: size,
 display:"inline-flex", alignItems:"center", justifyContent:"center",
 borderRadius: 999, border: baseBorder, background: hover ? hoverBg :"transparent",
 cursor:"pointer", marginLeft: 8, transition:"background .15s ease, transform .02s ease",
 boxShadow: hover ? shadow :"none", outline:"none",
 }}
 onMouseDown={(e) => (e.currentTarget.style.transform ="scale(.98)")}
 onMouseUp={(e) => (e.currentTarget.style.transform ="scale(1)")}
 >
 {children}
 </button>
 );
}

/* ---------- Languages ---------- */
const LANGS = [
 { code:"auto", label:"Auto (Detect)" },
 { code:"en-IN", label:"English (India)" },
 { code:"en-US", label:"English (US)" },
 { code:"te-IN", label:"తెలుగు (Telugu)" },
 { code:"hi-IN", label:"हिन्दी (Hindi)" },
 { code:"ta-IN", label:"தமிழ் (Tamil)" },
 { code:"kn-IN", label:"ಕನ್ನಡ (Kannada)" },
 { code:"ml-IN", label:"മലയാളം (Malayalam)" },
 { code:"mr-IN", label:"मराठी (Marathi)" },
 { code:"bn-IN", label:"বাংলা (Bengali)" },
 { code:"gu-IN", label:"ગુજરાતી (Gujarati)" },
 { code:"es-ES", label:"Español" },
 { code:"fr-FR", label:"Français" },
 { code:"de-DE", label:"Deutsch" },
 { code:"zh-CN", label:"中文 (简体)" },
];

/* ---------- Script / transliteration helpers ---------- */
const NON_LATIN_RE = /[^\u0000-\u007F]/;
const DEVANAGARI_RE = /[\u0900-\u097F]/;
const TELUGU_RE = /[\u0C00-\u0C7F]/;
const TAMIL_RE = /[\u0B80-\u0BFF]/;
const KANNADA_RE = /[\u0C80-\u0CFF]/;
const MALAYALAM_RE = /[\u0D00-\u0D7F]/;
const BENGALI_RE = /[\u0980-\u09FF]/;
const GUJARATI_RE = /[\u0A80-\u0AFF]/;
const MARATHI_RE = DEVANAGARI_RE; // Marathi uses Devanagari

function detectLangFromText(text, fallback ="en-IN") {
 if (DEVANAGARI_RE.test(text)) return"hi-IN";
 if (TELUGU_RE.test(text)) return"te-IN";
 if (TAMIL_RE.test(text)) return"ta-IN";
 if (KANNADA_RE.test(text)) return"kn-IN";
 if (MALAYALAM_RE.test(text)) return"ml-IN";
 if (BENGALI_RE.test(text)) return"bn-IN";
 if (GUJARATI_RE.test(text)) return"gu-IN";
 return fallback;
}

// very rough Devanagari→Latin fallback so English voices can say *something*
function roughTransliterateDevanagari(s) {
 const map = {
 'अ':'a','आ':'aa','इ':'i','ई':'ee','उ':'u','ऊ':'oo','ए':'e','ऐ':'ai','ओ':'o','औ':'au',
 'ा':'a','ि':'i','ी':'ee','ु':'u','ू':'oo','े':'e','ै':'ai','ो':'o','ौ':'au','ं':'n','ः':'h','ँ':'n',
 'क':'ka','ख':'kha','ग':'ga','घ':'gha','ङ':'nga',
 'च':'cha','छ':'chha','ज':'ja','झ':'jha','ञ':'nya',
 'ट':'ta','ठ':'tha','ड':'da','ढ':'dha','ण':'na',
 'त':'ta','थ':'tha','द':'da','ध':'dha','न':'na',
 'प':'pa','फ':'pha','ब':'ba','भ':'bha','म':'ma',
 'य':'ya','र':'ra','ल':'la','व':'va','श':'sha','ष':'sha','स':'sa','ह':'ha',
 'ृ':'ri','्':'', '०':'0','१':'1','२':'2','३':'3','४':'4','५':'5','६':'6','७':'7','८':'8','९':'9'
 };
 return s.split("").map(ch => map[ch] ?? ch).join("");
}

/* ---------- TTS helpers ---------- */
const waitForVoices = (maxMs = 3000) =>
 new Promise((resolve) => {
 const start = performance.now();
 const tick = () => {
 const list = window.speechSynthesis?.getVoices?.() || [];
 if (list.length || performance.now() - start > maxMs) return resolve(list);
 setTimeout(tick, 50);
 };
 tick();
 });

// Try to pick the best matching voice for a language
const chooseVoice = (voices, langCode) => {
 if (!voices || voices.length === 0) return null;
 const lc = (langCode ||"").toLowerCase();
 const base = lc.split("-")[0];

 // exact lang
 let v = voices.find(x => x.lang?.toLowerCase() === lc) ||
 voices.find(x => x.lang?.toLowerCase() === lc.replace("_","-"));
 if (v) return v;

 // base lang
 v = voices.find(x => x.lang?.toLowerCase()?.startsWith(base));
 if (v) return v;

 // Google voice often supports more scripts
 v = voices.find(x => x.name?.toLowerCase?.().includes("google"));
 if (v) return v;

 // default / first
 return voices.find(x => x.default) || voices[0];
};

function AdvancedVoiceAssistant() {
 const [isListening, setIsListening] = useState(false);
 const [messages, setMessages] = useState([]);
 const [isLoading, setIsLoading] = useState(false);

 const recognitionRef = useRef(null);
 const chatEndRef = useRef(null);

 // TTS state
 const currentUtteranceRef = useRef(null);
 const [ttsState, setTtsState] = useState({ isSpeaking: false, isPaused: false, text:"" });

 // Voices for TTS
 const [voices, setVoices] = useState([]);

 // Selected language
 const browserLang = (typeof navigator !=="undefined" && navigator.language) ||"en-IN";
 const defaultLang = LANGS.find(l => l.code === browserLang) ? browserLang :"auto";
 const [langCode, setLangCode] = useState(defaultLang);

 const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
 const HEADER_HEIGHT = 60;

 useEffect(() => {
 function handleResize() { setIsMobile(window.innerWidth <= 768); }
 window.addEventListener("resize", handleResize);
 return () => window.removeEventListener("resize", handleResize);
 }, []);

 // kill sideways scroll
 useEffect(() => {
 const prev = document.body.style.overflowX;
 document.body.style.overflowX ="hidden";
 return () => { document.body.style.overflowX = prev ||""; };
 }, []);

 /* ---------- Load TTS voices ---------- */
 useEffect(() => {
 if (typeof window ==="undefined" || !window.speechSynthesis) return;

 const setList = async () => {
 const list = await waitForVoices(3000);
 setVoices(list);
 if (list.length) {
 console.log("[TTS] voices:", list.map(v => ({ name: v.name, lang: v.lang })));
 }
 };
 setList();

 const onChange = () => setList();
 window.speechSynthesis.onvoiceschanged = onChange;
 return () => { window.speechSynthesis.onvoiceschanged = null; };
 }, []);

 /* ---------- Speech Recognition setup ---------- */
 useEffect(() => {
 const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
 if (!SpeechRecognition) {
 alert("Your browser does not support Speech Recognition.");
 return;
 }
 recognitionRef.current = new SpeechRecognition();
 recognitionRef.current.continuous = false;
 recognitionRef.current.interimResults = false;
 recognitionRef.current.lang = langCode ==="auto" ? (navigator.language ||"en-IN") : langCode;

 recognitionRef.current.onstart = () => setIsListening(true);
 recognitionRef.current.onend = () => setIsListening(false);
 recognitionRef.current.onerror = (event) => {
      console.error("Speech recognition error", event.error);
      if (event.error === 'network') {
        alert("Speech recognition network error. Please check your internet connection or try again later.");
      }
      setIsListening(false);
    };
 recognitionRef.current.onresult = (event) => {
 const transcript = event.results[0][0].transcript;
 addMessage("user", transcript);
 sendQueryToBackend(transcript);
 };

 return () => { try { recognitionRef.current && recognitionRef.current.stop(); } catch {} };
 }, []); // instantiate once

 // keep SR language in sync with selection
 useEffect(() => {
 if (!recognitionRef.current) return;
 recognitionRef.current.lang = langCode ==="auto" ? (navigator.language ||"en-IN") : langCode;
 }, [langCode]);

 const addMessage = (sender, text) => setMessages((prev) => [...prev, { sender, text }]);

 // Helper to normalize API URL
 function getChatUrl() {
 const base = (process.env.REACT_APP_API_URL ||"").trim();
 if (!base) return `${API_BASE_URL}/chat`;
 if (base.endsWith("/chat")) return base;
 return base.replace(/\/+$/,"") +"/chat";
 }

 const sendQueryToBackend = async (query) => {
 setIsLoading(true);
 try {
 const backendUrl = getChatUrl();

 const isAuto = langCode ==="auto";
 const langLabel = LANGS.find(l => l.code === langCode)?.label || langCode;
 const body = isAuto
 ? { message: query, langMode:"auto" }
 : { message: query, langMode:"fixed", langCode, langLabel };

 const response = await fetch(backendUrl, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 body: JSON.stringify(body),
 });
 if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);

 const data = await response.json();
 if (data.reply) {
 addMessage("bot", data.reply);
 // speak using server-returned language if provided
 handlePlay(data.reply, data.langCode);
 } else {
 addMessage("bot","No response.");
 }
 } catch (e) {
 console.error("Backend error:", e);
 addMessage("bot","Error communicating with backend.");
 } finally {
 setIsLoading(false);
 }
 };

 /* ---------- TTS ---------- */
 const synth = typeof window !=="undefined" ? window.speechSynthesis : null;

 const cancelSpeaking = () => {
 try { if (synth) synth.cancel(); }
 finally {
 currentUtteranceRef.current = null;
 setTtsState({ isSpeaking: false, isPaused: false, text:"" });
 }
 };

 // speak with multi-step fallbacks to avoid silence on non-Latin scripts
 const handlePlay = async (text, forcedLangCode) => {
 if (!synth || !text) return;

 // toggle for same text bubble
 if (ttsState.isSpeaking && ttsState.text === text) {
 if (ttsState.isPaused) {
 synth.resume(); setTtsState(s => ({ ...s, isPaused: false }));
 } else {
 synth.pause(); setTtsState(s => ({ ...s, isPaused: true }));
 }
 return;
 }

 cancelSpeaking();

 const list = voices.length ? voices : await waitForVoices(3000);

 const requestedLang =
 forcedLangCode ||
 (langCode ==="auto" ? detectLangFromText(text, navigator.language ||"en-IN")
 : langCode);

 // plan A: try a voice that matches requestedLang
 let chosen = chooseVoice(list, requestedLang);

 // if text is non-Latin and chosen voice is English → likely silent
 const textIsNonLatin = NON_LATIN_RE.test(text);
 const chosenIsEnglish = (chosen?.lang ||"").toLowerCase().startsWith("en");

 const trySpeak = (utter) =>
 new Promise((resolve) => {
 let spoke = false;
 utter.onstart = () => { spoke = true; setTtsState({ isSpeaking: true, isPaused: false, text }); };
 utter.onend = () => { resolve(spoke); cancelSpeaking(); };
 utter.onerror = () => { resolve(false); cancelSpeaking(); };
 try { synth.cancel(); } catch {}
 setTimeout(() => synth.speak(utter), 0);
 });

 // === IMPORTANT: Skip Attempt #1 if it's obviously wrong (non-Latin text + English voice) ===
 const shouldSkipAttempt1 = textIsNonLatin && chosenIsEnglish;

 // Attempt 1: choose matching voice (or let chosen speak) with requestedLang
 if (!shouldSkipAttempt1) {
 const utter = new SpeechSynthesisUtterance(text);
 if (chosen) utter.voice = chosen;
 utter.lang = chosen?.lang || requestedLang;
 utter.rate = 1; utter.pitch = 1; utter.volume = 1;

 console.log("[TTS] Attempt #1", { requested: requestedLang, using: utter.lang, voice: chosen?.name });
 const ok = await trySpeak(utter);
 if (ok) return;
 } else {
 console.log("[TTS] Skipping Attempt #1 (non-Latin text + English voice would be silent).");
 }

 // Attempt 2: force lang only (no voice) so browser/OS can auto-pick an appropriate voice
 if (textIsNonLatin) {
 const utter = new SpeechSynthesisUtterance(text);
 utter.lang = requestedLang; // no voice set
 utter.rate = 1; utter.pitch = 1; utter.volume = 1;

 console.log("[TTS] Attempt #2", { requested: requestedLang, using: utter.lang, voice:"(auto)" });
 const ok = await trySpeak(utter);
 if (ok) return;
 }

 // Attempt 3: last resort — transliterate (Hindi only here) and use EN voice
 if (DEVANAGARI_RE.test(text)) {
 const roman = roughTransliterateDevanagari(text);
 const enVoice = chooseVoice(list,"en-IN") || chooseVoice(list,"en-US");
 const utter = new SpeechSynthesisUtterance(roman);
 if (enVoice) utter.voice = enVoice;
 utter.lang = enVoice?.lang ||"en-IN";
 utter.rate = 1; utter.pitch = 1; utter.volume = 1;

 console.log("[TTS] Attempt #3 (transliteration)", { requested: utter.lang, voice: enVoice?.name });
 const ok = await trySpeak(utter);
 if (ok) return;
 }

 // If everything fails:
 console.warn("[TTS] All attempts failed (browser may lack voices / OS TTS).");
 };

 const handlePause = () => { if (synth && ttsState.isSpeaking && !ttsState.isPaused) { synth.pause(); setTtsState(s => ({ ...s, isPaused: true })); } };
 const handleResume = () => { if (synth && ttsState.isSpeaking && ttsState.isPaused) { synth.resume(); setTtsState(s => ({ ...s, isPaused: false })); } };
 const handleStop = () => cancelSpeaking();

 useEffect(() => () => cancelSpeaking(), []); // cleanup on unmount

 const toggleListening = () => {
 if (!recognitionRef.current) return;
 recognitionRef.current.lang = langCode ==="auto" ? (navigator.language ||"en-IN") : langCode;
 if (isListening) recognitionRef.current.stop();
 else recognitionRef.current.start();
 };

 useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

 const Waveform = () => (
 <svg width="100" height="24" viewBox="0 0 100 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display:"block" }}>
 {[...Array(12)].map((_, i) => (
 <rect key={i} x={i * 8} y={6} width={4} height={12} fill="#fcd34d"
 style={{ animation: `wave 1.2s ease-in-out infinite`, animationDelay: `${i * 0.1}s` }}/>
 ))}
 <style>{`@keyframes wave{0%,100%{transform:scaleY(1)}50%{transform:scaleY(2.5)}}`}</style>
 </svg>
 );

 const langLabel = LANGS.find(l => l.code === langCode)?.label || langCode;

 return (
 <div
 style={{
 width:"100%",
 minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
 padding: isMobile ?"8px 10px" :"16px 20px",
 display:"flex", flexDirection:"column",
 boxSizing:"border-box", overflowX:"hidden", overflowY:"auto",
 background:"#fef3c7",
 }}
 >
 {/* Header row: title + language selector */}
 <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap: 12, marginBottom: 8 }}>
 <h2 style={{ color:"#0a0a0a", fontWeight: 800, fontSize: isMobile ? 22 : 28, margin: 0 }}>
 <span style={{ color:"#f59e0b" }}>SkillGenie</span> Voice Assistant
 </h2>
 <label style={{ display:"flex", alignItems:"center", gap: 8 }}>
 <span style={{ fontSize: 12, color:"#111", opacity: .8 }}>Language</span>
 <select
 value={langCode}
 onChange={(e) => setLangCode(e.target.value)}
 style={{
 background:"#111827", color:"#fcd34d", border:"1px solid rgba(252,211,77,.35)",
 borderRadius: 999, padding:"8px 12px", outline:"none", cursor:"pointer",
 }}
 >
 {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
 </select>
 </label>
 </div>

 {/* Chat surface */}
 <div
 style={{
 flex: 1, display:"flex", flexDirection:"column", gap: isMobile ? 16 : 24,
 background:"#0a0a0a", borderRadius: 16, boxShadow:"0 0 20px rgba(252, 211, 77, 0.35)",
 padding: isMobile ? 12 : 20, boxSizing:"border-box",
 overflowY:"auto", overflowX:"hidden",
 }}
 >
 {messages.map(({ sender, text }, index) => {
 const isThisPlaying = ttsState.isSpeaking && ttsState.text === text;
 return (
 <div key={index} style={{ display:"flex", justifyContent: sender ==="user" ?"flex-end" :"flex-start", alignItems:"center", gap: isMobile ? 8 : 12 }}>
 {sender ==="user" && (
 <>
 <div
 style={{
 maxWidth:"78ch", backgroundColor:"#fcd34d", color:"#1a1a1a",
 padding: isMobile ?"10px 16px" :"12px 20px", borderRadius: 24,
 boxShadow:"0 0 10px #fcd34daa", display:"flex", alignItems:"center",
 gap: isMobile ? 8 : 12, fontSize: isMobile ? 14 : 16, whiteSpace:"pre-wrap", wordBreak:"break-word",
 }}
 >
 <Waveform />
 <span>{text}</span>
 <IconButton title={isThisPlaying ? (ttsState.isPaused ?"Resume" :"Pause") :"Play"} tone="light" size={isMobile ? 28 : 32} onClick={() => handlePlay(text)}>
 {isThisPlaying ? (ttsState.isPaused ? <PlayIcon color="#1a1a1a" /> : <PauseIcon color="#1a1a1a" />) : <VolumeIcon color="#1a1a1a" />}
 </IconButton>
 {isThisPlaying && (
 <IconButton title="Stop" tone="light" size={isMobile ? 28 : 32} onClick={handleStop}>
 <StopIcon color="#1a1a1a" />
 </IconButton>
 )}
 </div>
 <div style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius:"50%", border:"3px dashed #fcd34d", padding: 4, overflow:"hidden", flexShrink: 0 }}>
 <img src={userAvatar} alt="User" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
 </div>
 </>
 )}

 {sender ==="bot" && (
 <>
 <div style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius:"50%", border:"3px dashed #fcd34d", padding: 4, overflow:"hidden", flexShrink: 0 }}>
 <img src={botAvatar} alt="Bot" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
 </div>
 <div
 style={{
 maxWidth:"78ch", backgroundColor:"#1f2937", color:"#fcd34d",
 padding: isMobile ?"10px 16px" :"12px 20px", borderRadius: 24,
 boxShadow:"0 0 10px #fcd34d88", display:"flex", alignItems:"center",
 gap: isMobile ? 8 : 12, fontSize: isMobile ? 14 : 16, whiteSpace:"pre-wrap", wordBreak:"word-break",
 }}
 >
 <Waveform />
 <span>{text}</span>
 <IconButton title={isThisPlaying ? (ttsState.isPaused ?"Resume" :"Pause") :"Play"} tone="dark" size={isMobile ? 28 : 32} onClick={() => handlePlay(text)}>
 {isThisPlaying ? (ttsState.isPaused ? <PlayIcon color="#fcd34d" /> : <PauseIcon color="#fcd34d" />) : <VolumeIcon color="#fcd34d" />}
 </IconButton>
 {isThisPlaying && (
 <IconButton title="Stop" tone="dark" size={isMobile ? 28 : 32} onClick={handleStop}>
 <StopIcon color="#fcd34d" />
 </IconButton>
 )}
 </div>
 </>
 )}
 </div>
 );
 })}
 <div ref={chatEndRef} />
 </div>

 {/* Mic button */}
 <div style={{ display:"flex", justifyContent:"center", paddingTop: 16 }}>
 <button
 onClick={toggleListening}
 disabled={isLoading}
 style={{
 padding: isMobile ?"12px 24px" :"14px 30px",
 fontSize: isMobile ? 16 : 18,
 backgroundColor: isListening ?"#ff4d4d" :"#fcd34d",
 color: isListening ?"#fff" :"#1a1a1a",
 border:"none",
 borderRadius: 40,
 cursor: isLoading ?"not-allowed" :"pointer",
 fontWeight: 600,
 boxShadow: isListening ?"0 0 15px #ff4d4d" :"0 0 15px #fcd34d",
 transition:"background-color 0.3s ease",
 userSelect:"none",
 }}
 >
 {isLoading ?"Processing…" : isListening ? `🎙️ Listening (${langLabel})… Click to Stop` :"🎤 Click to Speak"}
 </button>
 </div>

 {/* Mini TTS Player */}
 {ttsState.isSpeaking && (
 <div
 style={{
 position:"sticky", bottom: 8, marginTop: 10, alignSelf:"center",
 display:"flex", alignItems:"center", gap: 10,
 background:"#111827", color:"#fcd34d",
 borderRadius: 999, padding:"8px 14px",
 boxShadow:"0 0 12px rgba(252,211,77,.5)", maxWidth:"90vw",
 }}
 title={ttsState.text}
 >
 <span style={{ maxWidth:"60ch", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", opacity: .9 }}>
 🔊 {ttsState.isPaused ?"Paused" :"Speaking"}
 </span>
 {ttsState.isPaused ? (
 <IconButton title="Resume" tone="dark" size={28} onClick={handleResume}><PlayIcon color="#fcd34d" /></IconButton>
 ) : (
 <IconButton title="Pause" tone="dark" size={28} onClick={handlePause}><PauseIcon color="#fcd34d" /></IconButton>
 )}
 <IconButton title="Stop" tone="dark" size={28} onClick={handleStop}><StopIcon color="#fcd34d" /></IconButton>
 </div>
 )}
 </div>
 );
}

export default AdvancedVoiceAssistant;








